import { useState, useEffect, KeyboardEvent } from 'react';
import { useFormContext } from 'react-hook-form';
import {
  NEGATIVE_NUMBERS_FLOAT_DOT_OR_COMMA_OR_PLUS,
  NUMBERS_FLOAT_DOT_OR_COMMA_OR_PLUS,
} from '../constants';
import {
  currencyFormatter,
  parseCurrency,
  parsePercent,
  percentageFormat,
  setDecimal,
} from '../helpers';

interface Props {
  name: string;
  isCurrency?: boolean;
  isPercent?: boolean;
  isDecimal?: boolean;
  isPurchase?: boolean;
  isSelling?: boolean;
  isCellphone?: boolean;
  precision?: number;
  allowNegativeNumber?: boolean;
  customSymbol?: string;
}

interface Return {
  format: (value: number) => string;
  setIsFocus: (isFocus: boolean) => void;
  setCheckedFalsyValue: (isCheckedFalsyValue: boolean) => void;
  handleKeyPress: (event: KeyboardEvent<HTMLInputElement>) => void;
  parse: (value: string) => number | string;
  checkedFalsyValue: boolean;
  inputValue: number | string;
}

export const useNumericInput = ({
  name,
  isDecimal,
  isCurrency,
  isPercent,
  isPurchase,
  isSelling,
  isCellphone,
  precision,
  allowNegativeNumber,
  customSymbol = '€',
}: Props): Return => {
  const { watch } = useFormContext();
  const inputValue = watch(name);

  const [numberDecimal, setNumberDecimal] = useState<number>(0);
  const [isFocus, setIsFocus] = useState<boolean>(false);
  const [lastValue, setLastValue] = useState<string>(inputValue ?? 0);
  const [checkedFalsyValue, setCheckedFalsyValue] = useState<boolean>(false);

  const getValueNumber = (value: number | string) => {
    if (typeof value === 'number') return value;
    const valueString = String(value).replace('.', '').replace(',', '.');
    let output = 0;
    if (!value) return isCellphone ? '' : output;
    if (isCurrency)
      output = parseCurrency(valueString, allowNegativeNumber ?? false);
    if (isPercent) output = parsePercent(valueString);
    if (isDecimal || (!isCurrency && !isPercent)) output = Number(valueString);
    return output;
  };
  const [lastValueNumber, setLastValueNumber] = useState<number | string>(
    getValueNumber(inputValue),
  );

  const getFormattedValue = (value: number): string => {
    if (isCurrency)
      return currencyFormatter({
        value,
        options: numberDecimal,
        customSymbol,
      });
    if (isPercent) return percentageFormat(value);
    return setDecimal(value, numberDecimal);
  };

  const setNumberFormat = (val: number): string => {
    if (isCurrency)
      return String(
        getValueNumber(
          getFormattedValue(val).replace(/[€]/g, '').replace(/\s/g, ''),
        ),
      );
    if (isPercent) return getFormattedValue(val).replace(/\s[%]/g, '');
    if (isCellphone) return String(getValueNumber(getFormattedValue(val)));
    return setDecimal(val, numberDecimal);
  };

  const format = (val: number) => {
    if (!isCellphone && isNaN(val)) return lastValue;
    if (isFocus) return setNumberFormat(val);
    return getFormattedValue(val);
  };

  const parse = (value: string): number | string => {
    setLastValue(value);
    const output = getValueNumber(value);
    if (!isCellphone && isNaN(Number(output))) {
      return lastValueNumber;
    }
    setLastValueNumber(output);
    return output;
  };

  const handleKeyPress = (event: KeyboardEvent<HTMLInputElement>) => {
    if (
      event.key === 'Backspace' ||
      event.key === 'Delete' ||
      event.key === 'ArrowRight' ||
      event.key === 'ArrowLeft'
    ) {
      return false;
    }
    if (allowNegativeNumber) {
      if (!NEGATIVE_NUMBERS_FLOAT_DOT_OR_COMMA_OR_PLUS.test(event.key)) {
        event.preventDefault();
      }
    } else {
      if (!NUMBERS_FLOAT_DOT_OR_COMMA_OR_PLUS.test(event.key)) {
        event.preventDefault();
      }
    }
  };

  useEffect(() => {
    let decimals = precision ?? 0;
    if (isCurrency) {
      if (!isPurchase && !isSelling) decimals = 2;
    }
    if (isDecimal || isPercent) decimals = 2;
    return setNumberDecimal(decimals);
  }, []);

  return {
    format,
    setIsFocus,
    parse,
    handleKeyPress,
    inputValue,
    checkedFalsyValue,
    setCheckedFalsyValue,
  };
};

export default useNumericInput;
